/*
 * component-shared-accordions.js — Slice 18 Phase A.
 *
 * Vanilla disclosure-widget behavior for the shared accordions block +
 * accordions section. Loaded via <script src=… defer> from the consumer
 * (browser dedupes by URL). Single document-level event delegation handles
 * all instances on the page.
 *
 * Behavior:
 *   - click on a .shared-accordion__trigger: toggle the parent .shared-accordion__item's
 *     `is-open` class, update aria-expanded, toggle `hidden` on the panel.
 *   - if wrapper carries data-single-open="true", close other open siblings.
 *   - on page load: open the first item per wrapper that carries
 *     data-open-first="true" AND has no item already in is-open state.
 *
 * Animation:
 *   - max-block-size transition runs via CSS. JS sets --acc-panel-max to
 *     the panel's scrollHeight on open (so it transitions to a real value),
 *     then back to 0 on close.
 *   - `hidden` attribute is added after the close transition via a one-shot
 *     transitionend listener filtered to max-block-size on the panel
 *     itself. Reverted to advisor-flagged transitionend pattern (cleaner
 *     than setTimeout(parseInt(transitionDuration)) which returned 0 for
 *     "0.25s" values).
 *
 * Rules in force:
 *   Rule J1  — no inline event handlers (addEventListener only)
 *   Rule J7  — document-level listener; no per-item observers required
 *   Rule J8  — no window.* namespace; pure IIFE
 *   Rule J12 — IIFE-dedup guard for multi-instance safety
 */
(function () {
  if (window.__BLOCKDEDUP_SHARED_ACCORDIONS__) return;
  window.__BLOCKDEDUP_SHARED_ACCORDIONS__ = true;

  function findItem(el) {
    return el.closest('[data-acc-item]');
  }
  function findWrapper(el) {
    return el.closest('[data-shared-accordions]');
  }

  function onCloseTransitionEnd(e) {
    if (e.propertyName !== 'max-block-size') return;
    var panel = e.currentTarget;
    panel.removeEventListener('transitionend', onCloseTransitionEnd);
    var item = panel.closest('[data-acc-item]');
    if (item && !item.classList.contains('is-open')) {
      panel.setAttribute('hidden', '');
    }
  }

  function setOpen(item, open) {
    var trigger = item.querySelector('.shared-accordion__trigger');
    var panel   = item.querySelector('.shared-accordion__panel');
    if (!trigger || !panel) return;

    // Always remove any pending close listener — covers the rapid reopen case
    // where the user clicks before the close transition completes.
    panel.removeEventListener('transitionend', onCloseTransitionEnd);

    if (open) {
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
      panel.style.setProperty('--acc-panel-max', panel.scrollHeight + 'px');
    } else {
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.setProperty('--acc-panel-max', '0px');
      panel.addEventListener('transitionend', onCloseTransitionEnd);
    }
  }

  function onClick(e) {
    var trigger = e.target.closest('.shared-accordion__trigger');
    if (!trigger) return;
    var wrapper = findWrapper(trigger);
    if (!wrapper) return;
    var item = findItem(trigger);
    if (!item) return;

    var isOpen = item.classList.contains('is-open');

    if (!isOpen && wrapper.getAttribute('data-single-open') === 'true') {
      var siblings = wrapper.querySelectorAll('[data-acc-item].is-open');
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] !== item) setOpen(siblings[i], false);
      }
    }

    setOpen(item, !isOpen);
  }

  function initOpenFirst() {
    var wrappers = document.querySelectorAll('[data-shared-accordions][data-open-first="true"]');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      if (wrapper.querySelector('[data-acc-item].is-open')) continue;
      var first = wrapper.querySelector('[data-acc-item]');
      if (first) setOpen(first, true);
    }
  }

  function init() {
    document.addEventListener('click', onClick);
    // Items that ship is-open from acc_open_by_default need their max set so
    // the CSS transition has a concrete value to animate FROM on close.
    var openItems = document.querySelectorAll('[data-shared-accordions] [data-acc-item].is-open');
    for (var j = 0; j < openItems.length; j++) {
      var panel = openItems[j].querySelector('.shared-accordion__panel');
      if (panel) panel.style.setProperty('--acc-panel-max', panel.scrollHeight + 'px');
    }
    initOpenFirst();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
